import { normalizeMermaidSvg } from '@/lib/svg-image';
import mermaid from 'mermaid';
import { useEffect, useId, useMemo, useState } from 'react';
import { stripFence } from './utils';

/**
 * mermaid 전역 설정은 마지막 initialize 가 이긴다. 테마(다크/라이트)가 바뀌면 재초기화해야
 * 하므로 마지막 적용 테마를 추적한다. `useMaxWidth:false` 로 모든 다이어그램이 컨테이너 폭에
 * 맞춰 축소되지 않고 자연 크기로 렌더된다(가독성).
 */
let lastTheme: 'dark' | 'default' | null = null;
function ensureInit(dark: boolean) {
  const theme = dark ? 'dark' : 'default';
  if (lastTheme === theme) return;
  mermaid.initialize({
    startOnLoad: false,
    theme,
    securityLevel: 'strict',
    gantt: { useMaxWidth: false },
    flowchart: { useMaxWidth: false },
    sequence: { useMaxWidth: false },
    er: { useMaxWidth: false },
    journey: { useMaxWidth: false },
    class: { useMaxWidth: false },
    state: { useMaxWidth: false },
    pie: { useMaxWidth: false },
    mindmap: { useMaxWidth: false },
    timeline: { useMaxWidth: false },
    quadrantChart: { useMaxWidth: false },
    requirement: { useMaxWidth: false },
    gitGraph: { useMaxWidth: false },
  });
  lastTheme = theme;
}

interface MermaidRender {
  /** 렌더 성공 시 정규화된 SVG 문자열, 아직 렌더 전이면 null. */
  svg: string | null;
  /** 문법 오류 등 렌더 실패 메시지, 성공이면 null. */
  error: string | null;
  /** 펜스가 제거된 실제 mermaid 소스. 폴백 코드뷰/복사에 사용. */
  cleaned: string;
}

interface Opts {
  /** 다크 테마 여부 — 바뀌면 mermaid 재초기화 후 재렌더. */
  dark?: boolean;
}

/**
 * mermaid 소스를 SVG 로 렌더한다. `MermaidArtifact`(우측 패널) 와 `InlineMermaid`(채팅 본문)
 * 가 공유. source 또는 dark 가 바뀔 때마다 재렌더하며, 진행 중 변경/unmount 시 stale 결과는 무시.
 */
export function useMermaidSvg(source: string, opts: Opts = {}): MermaidRender {
  const dark = opts.dark ?? false;
  const reactId = useId();
  const safeId = `mermaid-${reactId.replace(/[^a-zA-Z0-9]/g, '')}`;
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cleaned = useMemo(() => stripFence(source, 'mermaid') || source, [source]);

  useEffect(() => {
    ensureInit(dark);
    let cancelled = false;
    void (async () => {
      try {
        const { svg } = await mermaid.render(safeId, cleaned);
        if (!cancelled) {
          setSvg(normalizeMermaidSvg(svg));
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '렌더 실패');
          setSvg(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cleaned, safeId, dark]);

  return { svg, error, cleaned };
}
